import React, { useState, useEffect, useRef, DragEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { showToast } from '../../components/Toast';
import BACKEND_URL from '../../config.js';
import '../../styles/aziendale.css';

interface Utente { id: number; nome: string; }
interface Societa { id: number; nome: string; }
interface UltimaVersione { id: number; versione: number; dataScadenza: string; nomeFile: string; uploadedAt: string; }
interface Documento {
  id: number;
  societaId: number;
  societa: Societa;
  nome: string;
  responsabile: { id: number; nome: string; email: string };
  giorniPreavviso: number;
  attivo: boolean;
  ultimaVersione: UltimaVersione | null;
  statoScadenza: 'VERDE' | 'GIALLO' | 'ROSSO';
}

const BADGE: Record<string, { bg: string; color: string; label: string }> = {
  VERDE: { bg: '#dcfce7', color: '#16a34a', label: 'In scadenza OK' },
  GIALLO: { bg: '#fef9c3', color: '#854d0e', label: 'In preavviso' },
  ROSSO: { bg: '#fef2f2', color: '#dc2626', label: 'Scaduto' },
};


export default function DocumentiRicorrentiPage() {
  const navigate = useNavigate();
  const [documenti, setDocumenti] = useState<Documento[]>([]);
  // Utenti selezionabili come responsabile, per società (caricati a richiesta)
  const [utentiPerSocieta, setUtentiPerSocieta] = useState<Record<number, Utente[]>>({});
  const [isLoading, setIsLoading] = useState(true);

  const {
    societaId: filterSocietaId,
    setSocietaId: setFilterSocietaId,
    listaSocieta: societaList,
    isUnica,
    pronta,
  } = useFiltroSocieta();

  // Modal Nuovo Documento
  const [showModalNew, setShowModalNew] = useState(false);
  const [newSocietaId, setNewSocietaId] = useState('');
  const [newNome, setNewNome] = useState('');
  const [newResponsabileId, setNewResponsabileId] = useState('');
  const [newGiorniPreavviso, setNewGiorniPreavviso] = useState('30');
  const [newDataScadenza, setNewDataScadenza] = useState('');
  const [newNote, setNewNote] = useState('');
  const [newFile, setNewFile] = useState<File | null>(null);
  const [isSavingNew, setIsSavingNew] = useState(false);
  const fileNewRef = useRef<HTMLInputElement>(null);

  // Modal Nuova Versione
  const [showModalVer, setShowModalVer] = useState(false);
  const [verDocId, setVerDocId] = useState<number | null>(null);
  const [verDocNome, setVerDocNome] = useState('');
  const [verDocVersione, setVerDocVersione] = useState(0);
  const [verDataScadenza, setVerDataScadenza] = useState('');
  const [verNote, setVerNote] = useState('');
  const [verFile, setVerFile] = useState<File | null>(null);
  const [isSavingVer, setIsSavingVer] = useState(false);
  const fileVerRef = useRef<HTMLInputElement>(null);

  // Modal Cambia Responsabile
  const [showModalResp, setShowModalResp] = useState(false);
  const [respDocId, setRespDocId] = useState<number | null>(null);
  const [respDocNome, setRespDocNome] = useState('');
  const [respSocietaId, setRespSocietaId] = useState<number | null>(null);
  const [newRespId, setNewRespId] = useState('');
  const [isSavingResp, setIsSavingResp] = useState(false);

  // Auto-fill newSocietaId: per utente mono-società usa l'unica; per topbar fissa usa filterSocietaId
  useEffect(() => {
    if (isUnica && societaList.length === 1) setNewSocietaId(societaList[0].id.toString());
    else if (filterSocietaId) setNewSocietaId(filterSocietaId);
  }, [isUnica, societaList, filterSocietaId]);

  // Carica gli utenti della società del modale aperto (nuovo documento o cambio responsabile)
  useEffect(() => {
    const ids = [newSocietaId, respSocietaId]
      .map(v => parseInt(String(v ?? ''), 10))
      .filter(id => id && !utentiPerSocieta[id]);
    ids.forEach(id => {
      // @api GET /api/utenti/selezionabili?societaId={id} -> {"success":"true","data":[{"id":"number","nome":"string"}]}
      apiFetch<{ success: boolean; data: Utente[] }>(`/api/utenti/selezionabili?societaId=${id}`)
        .then(d => setUtentiPerSocieta(prev => ({ ...prev, [id]: d.data || [] })))
        .catch(err => {
          console.error('[DocumentiRicorrentiPage][utentiSelezionabili]', err);
          showToast('Errore nel caricamento degli utenti', 'error');
        });
    });
  }, [newSocietaId, respSocietaId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchDocumenti();
  }, [pronta, filterSocietaId]); // eslint-disable-line

  const fetchDocumenti = async () => {
    if (!pronta) return;
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      if (filterSocietaId) params.append('societaId', filterSocietaId);
      const data = await apiFetch<Documento[]>(`/api/documenti-ricorrenti?${params}`);
      setDocumenti(data);
    } catch (err) { console.error(err); }
    finally { setIsLoading(false); }
  };

  // Utenti della società selezionata nel modal
  const utentiDellaSocieta = (societaId: string | number | null) =>
    (societaId ? utentiPerSocieta[parseInt(String(societaId), 10)] : undefined) ?? [];

  // ── Nuovo Documento ───────────────────────────────────────────────────
  const handleDragNew = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files[0]) setNewFile(e.dataTransfer.files[0]);
  };
  const handleDragVer = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files[0]) setVerFile(e.dataTransfer.files[0]);
  };

  const handleSubmitNew = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFile) { alert('Seleziona un file'); return; }
    if (!isUnica && !newSocietaId) { alert('Seleziona la società'); return; }
    if (!newResponsabileId) { alert('Seleziona il responsabile'); return; }
    try {
      setIsSavingNew(true);
      const formData = new FormData();
      formData.append('societaId', newSocietaId || filterSocietaId || societaList[0]?.id.toString());
      formData.append('nome', newNome);
      formData.append('responsabileId', newResponsabileId);
      formData.append('giorniPreavviso', newGiorniPreavviso);
      formData.append('dataScadenza', newDataScadenza);
      formData.append('note', newNote);
      formData.append('file', newFile);
      const res = await fetch(`${BACKEND_URL}/api/documenti-ricorrenti`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: formData,
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error || 'Errore'); }
      setShowModalNew(false);
      resetNewForm();
      fetchDocumenti();
    } catch (err: any) { alert(err.message); }
    finally { setIsSavingNew(false); }
  };

  const resetNewForm = () => {
    setNewNome(''); setNewResponsabileId(''); setNewGiorniPreavviso('30');
    setNewDataScadenza(''); setNewNote(''); setNewFile(null);
    if (fileNewRef.current) fileNewRef.current.value = '';
    if (isUnica && societaList.length === 1) setNewSocietaId(societaList[0].id.toString());
    else setNewSocietaId('');
  };

  // ── Nuova Versione ────────────────────────────────────────────────────
  const openModalVer = (doc: Documento) => {
    setVerDocId(doc.id);
    setVerDocNome(doc.nome);
    setVerDocVersione(doc.ultimaVersione?.versione || 0);
    setVerDataScadenza(''); setVerNote(''); setVerFile(null);
    if (fileVerRef.current) fileVerRef.current.value = '';
    setShowModalVer(true);
  };

  const handleSubmitVer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!verFile) { alert('Seleziona un file'); return; }
    if (!verDataScadenza) { alert('Inserisci la data di scadenza'); return; }
    try {
      setIsSavingVer(true);
      const formData = new FormData();
      formData.append('dataScadenza', verDataScadenza);
      formData.append('note', verNote);
      formData.append('file', verFile);
      const res = await fetch(`${BACKEND_URL}/api/documenti-ricorrenti/${verDocId}/versioni`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: formData,
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error || 'Errore'); }
      setShowModalVer(false);
      fetchDocumenti();
    } catch (err: any) { alert(err.message); }
    finally { setIsSavingVer(false); }
  };

  // ── Cambia Responsabile ───────────────────────────────────────────────
  const openModalResp = (doc: Documento) => {
    setRespDocId(doc.id);
    setRespDocNome(doc.nome);
    setRespSocietaId(doc.societaId);
    setNewRespId(doc.responsabile.id.toString());
    setShowModalResp(true);
  };

  const handleSubmitResp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRespId) { alert('Seleziona il responsabile'); return; }
    try {
      setIsSavingResp(true);
      await apiFetch(`/api/documenti-ricorrenti/${respDocId}/responsabile`, {
        method: 'PUT',
        body: JSON.stringify({ responsabileId: parseInt(newRespId, 10) }),
      });
      setShowModalResp(false);
      fetchDocumenti();
    } catch (err: any) { alert(err.message); }
    finally { setIsSavingResp(false); }
  };

  return (
    <Layout title="Documenti Ricorrenti">
      {/* ── Barra azioni ── */}
      <div className="aziendale-action-bar">
        <div className="aziendale-action-bar-left">
          {!isUnica && (
            <select value={filterSocietaId} onChange={e => setFilterSocietaId(e.target.value)} className="aziendale-filter-select">
              <option value="">Tutte le società</option>
              {societaList.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
          )}
        </div>
        <button onClick={() => { resetNewForm(); setShowModalNew(true); }} className="aziendale-btn-new">
          + Nuovo Documento
        </button>
      </div>

      {/* ── Tabella ── */}
      <div className="bop-card">
        {isLoading ? (
          <div className="aziendale-loading">Caricamento...</div>
        ) : documenti.length === 0 ? (
          <div className="aziendale-empty">
            Nessun documento ricorrente trovato.
          </div>
        ) : (
          <div className="aziendale-table-wrapper">
            <table className="aziendale-table">
              <thead>
                <tr>
                  {['Nome', !isUnica ? 'Società' : '', 'Responsabile', 'Ultima Versione', 'Scadenza', 'Preavviso', 'Azioni']
                    .filter(Boolean).map(h => (
                      <th key={h}>{h}</th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {documenti.map(doc => {
                  const badge = BADGE[doc.statoScadenza];
                  const uv = doc.ultimaVersione;
                  return (
                    <tr key={doc.id}>
                      <td className="aziendale-table-cell-bold">{doc.nome}</td>
                      {!isUnica && <td className="aziendale-table-cell-muted">{doc.societa.nome}</td>}
                      <td className="aziendale-table-cell-secondary">{doc.responsabile.nome}</td>
                      <td className="aziendale-table-cell-muted">
                        {uv ? `v${uv.versione} — ${new Date(uv.uploadedAt).toLocaleDateString('it-IT')}` : <em className="aziendale-table-cell-em">nessuna</em>}
                      </td>
                      <td>
                        {uv ? (
                          <span className={`aziendale-badge-scadenza-data aziendale-badge-scadenza-data--${doc.statoScadenza.toLowerCase()}`}>
                            {new Date(uv.dataScadenza).toLocaleDateString('it-IT')}
                          </span>
                        ) : <em className="aziendale-table-cell-em">—</em>}
                      </td>
                      <td>
                        <span className={`aziendale-badge-preavviso aziendale-badge-preavviso--${doc.statoScadenza.toLowerCase()}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td>
                        <div className="aziendale-table-actions">
                          <button onClick={() => navigate(`/aziendale/documenti-ricorrenti/${doc.id}`)} className="aziendale-btn-dettaglio">
                            Dettaglio
                          </button>
                          <button onClick={() => openModalVer(doc)} className="aziendale-btn-versione">
                            Nuova Versione
                          </button>
                          <button onClick={() => openModalResp(doc)} className="aziendale-btn-responsabile">
                            Responsabile
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Modal: Nuovo Documento ── */}
      {showModalNew && (
        <div className="aziendale-modal-overlay">
          <div className="aziendale-modal">
            <div className="aziendale-modal-header">
              <h2 className="aziendale-modal-title">Nuovo Documento Ricorrente</h2>
              <button onClick={() => setShowModalNew(false)} className="aziendale-modal-close">×</button>
            </div>
            <form onSubmit={handleSubmitNew} className="aziendale-modal-form">
              {!isUnica && (
                <div className="aziendale-form-field">
                  <label className="aziendale-form-label">Società *</label>
                  <select value={newSocietaId} onChange={e => { setNewSocietaId(e.target.value); setNewResponsabileId(''); }} className="aziendale-form-input" required>
                    <option value="">Seleziona società...</option>
                    {societaList.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
                  </select>
                </div>
              )}
              <div className="aziendale-form-field">
                <label className="aziendale-form-label">Nome documento *</label>
                <input type="text" value={newNome} onChange={e => setNewNome(e.target.value)} className="aziendale-form-input" placeholder="Es. Documento di Valutazione dei Rischi" required />
              </div>
              <div className="aziendale-form-row-2">
                <div className="aziendale-form-field">
                  <label className="aziendale-form-label">Responsabile *</label>
                  <select value={newResponsabileId} onChange={e => setNewResponsabileId(e.target.value)} className="aziendale-form-input" required>
                    <option value="">Seleziona...</option>
                    {utentiDellaSocieta(newSocietaId).map(u => (
                      <option key={u.id} value={u.id}>{u.nome}</option>
                    ))}
                  </select>
                </div>
                <div className="aziendale-form-field">
                  <label className="aziendale-form-label">Giorni preavviso *</label>
                  <input type="number" value={newGiorniPreavviso} onChange={e => setNewGiorniPreavviso(e.target.value)} className="aziendale-form-input" min="1" max="365" required />
                </div>
              </div>
              <div className="aziendale-form-field">
                <label className="aziendale-form-label">Data scadenza *</label>
                <input type="date" value={newDataScadenza} onChange={e => setNewDataScadenza(e.target.value)} className="aziendale-form-input" required />
              </div>
              <div className="aziendale-form-field">
                <label className="aziendale-form-label">File (prima versione) *</label>
                <div onDragOver={e => e.preventDefault()} onDrop={handleDragNew}
                  onClick={() => fileNewRef.current?.click()}
                  className={newFile ? 'aziendale-file-dropzone aziendale-file-dropzone-active' : 'aziendale-file-dropzone'}>
                  <input type="file" ref={fileNewRef} className="bop-sr-only" onChange={e => e.target.files && setNewFile(e.target.files[0])} />
                  {newFile ? (
                    <span className="aziendale-file-selected">{newFile.name}</span>
                  ) : (
                    <span className="aziendale-file-prompt"><span className="aziendale-file-prompt-link">Clicca</span> o trascina il file qui</span>
                  )}
                </div>
              </div>
              <div className="aziendale-form-field">
                <label className="aziendale-form-label">Note (opzionale)</label>
                <textarea value={newNote} onChange={e => setNewNote(e.target.value)} className="aziendale-form-textarea" placeholder="Note sul documento..." />
              </div>
              <div className="aziendale-modal-footer">
                <button type="button" onClick={() => setShowModalNew(false)} className="aziendale-btn-cancel">
                  Annulla
                </button>
                <button type="submit" disabled={isSavingNew} className="aziendale-btn-submit">
                  {isSavingNew ? 'Salvataggio...' : 'Salva e Carica'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: Nuova Versione ── */}
      {showModalVer && (
        <div className="aziendale-modal-overlay">
          <div className="aziendale-modal aziendale-modal-md">
            <div className="aziendale-modal-header aziendale-modal-header-compact">
              <h2 className="aziendale-modal-title">Nuova Versione</h2>
              <button onClick={() => setShowModalVer(false)} className="aziendale-modal-close">×</button>
            </div>
            <p className="aziendale-modal-subtitle">
              <strong>{verDocNome}</strong> — versione corrente: {verDocVersione > 0 ? `v${verDocVersione}` : 'nessuna'}
            </p>
            <form onSubmit={handleSubmitVer} className="aziendale-modal-form">
              <div className="aziendale-form-field">
                <label className="aziendale-form-label">Nuova data scadenza *</label>
                <input type="date" value={verDataScadenza} onChange={e => setVerDataScadenza(e.target.value)} className="aziendale-form-input" required />
              </div>
              <div className="aziendale-form-field">
                <label className="aziendale-form-label">File *</label>
                <div onDragOver={e => e.preventDefault()} onDrop={handleDragVer}
                  onClick={() => fileVerRef.current?.click()}
                  className={verFile ? 'aziendale-file-dropzone aziendale-file-dropzone-active' : 'aziendale-file-dropzone'}>
                  <input type="file" ref={fileVerRef} className="bop-sr-only" onChange={e => e.target.files && setVerFile(e.target.files[0])} />
                  {verFile ? (
                    <span className="aziendale-file-selected">{verFile.name}</span>
                  ) : (
                    <span className="aziendale-file-prompt"><span className="aziendale-file-prompt-link">Clicca</span> o trascina il file qui</span>
                  )}
                </div>
              </div>
              <div className="aziendale-form-field">
                <label className="aziendale-form-label">Note (opzionale)</label>
                <textarea value={verNote} onChange={e => setVerNote(e.target.value)} className="aziendale-form-textarea" placeholder="Note sulla versione..." />
              </div>
              <div className="aziendale-modal-footer">
                <button type="button" onClick={() => setShowModalVer(false)} className="aziendale-btn-cancel">
                  Annulla
                </button>
                <button type="submit" disabled={isSavingVer} className="aziendale-btn-submit">
                  {isSavingVer ? 'Caricamento...' : 'Carica Nuova Versione'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: Cambia Responsabile ── */}
      {showModalResp && (
        <div className="aziendale-modal-overlay">
          <div className="aziendale-modal aziendale-modal-sm">
            <div className="aziendale-modal-header aziendale-modal-header-compact">
              <h2 className="aziendale-modal-title">Cambia Responsabile</h2>
              <button onClick={() => setShowModalResp(false)} className="aziendale-modal-close">×</button>
            </div>
            <p className="aziendale-modal-subtitle">{respDocNome}</p>
            <form onSubmit={handleSubmitResp} className="aziendale-modal-form">
              <div className="aziendale-form-field">
                <label className="aziendale-form-label">Nuovo responsabile *</label>
                <select value={newRespId} onChange={e => setNewRespId(e.target.value)} className="aziendale-form-input" required>
                  <option value="">Seleziona...</option>
                  {utentiDellaSocieta(respSocietaId).map(u => (
                    <option key={u.id} value={u.id}>{u.nome}</option>
                  ))}
                </select>
              </div>
              <div className="aziendale-modal-footer">
                <button type="button" onClick={() => setShowModalResp(false)} className="aziendale-btn-cancel">
                  Annulla
                </button>
                <button type="submit" disabled={isSavingResp} className="aziendale-btn-submit">
                  {isSavingResp ? 'Salvataggio...' : 'Salva'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
